import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  CollectionReference,
  deleteDoc,
  doc,
  DocumentData,
  DocumentReference,
  Firestore,
  getDoc,
  getDocFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  OrderByDirection,
  query,
  QueryConstraint,
  setDoc,
  Unsubscribe,
  UpdateData,
  updateDoc,
  where,
  WhereFilterOp,
  WithFieldValue,
} from 'firebase/firestore';
import { Observable } from 'rxjs';

import { FIREBASE_HANDLES } from './firebase.providers';

/**
 * Filter spec used by {@link FirestoreService.query}.
 *
 * NOTE on naming: the issue mentions a "FieldFilter" pattern carried over
 * from the anydataset Python codebase. The Firestore JS modular SDK does not
 * expose a `FieldFilter` class — the equivalent (and non-deprecated) pattern
 * is `where('field', '==', value)`. We surface it as a {@link FieldFilter}
 * record here so callers describe filters declaratively (mirroring the
 * Python signature) and the wrapper translates them to the modular API.
 */
export interface FieldFilter {
  readonly field: string;
  readonly op: WhereFilterOp;
  readonly value: unknown;
}

export interface QuerySpec {
  readonly filters?: readonly FieldFilter[];
  readonly orderBy?: readonly { field: string; direction?: OrderByDirection }[];
  readonly limit?: number;
}

/**
 * Thin Firestore wrapper that:
 *  - Centralizes the FieldFilter-style query construction so feature code
 *    never builds raw `where(...)` calls (consistency with anydataset).
 *  - Exposes `onSnapshot` as an RxJS `Observable` for ergonomic subscription.
 */
@Injectable({ providedIn: 'root' })
export class FirestoreService {
  readonly db: Firestore = inject(FIREBASE_HANDLES).firestore;

  collection<T = DocumentData>(path: string): CollectionReference<T> {
    return collection(this.db, path) as CollectionReference<T>;
  }

  doc<T = DocumentData>(path: string): DocumentReference<T> {
    return doc(this.db, path) as DocumentReference<T>;
  }

  /** Build a Firestore query from a declarative {@link QuerySpec}. */
  buildQuery<T = DocumentData>(collectionPath: string, spec: QuerySpec = {}) {
    const constraints: QueryConstraint[] = [];

    for (const f of spec.filters ?? []) {
      constraints.push(where(f.field, f.op, f.value));
    }
    for (const o of spec.orderBy ?? []) {
      constraints.push(orderBy(o.field, o.direction));
    }
    if (spec.limit !== undefined) {
      constraints.push(limit(spec.limit));
    }

    return query(this.collection<T>(collectionPath), ...constraints);
  }

  async getDoc<T = DocumentData>(path: string): Promise<T | undefined> {
    const snap = await getDoc(this.doc<T>(path));
    return snap.exists() ? (snap.data() as T) : undefined;
  }

  /**
   * Like {@link getDoc}, but from the server only: it rejects when the server
   * cannot be reached instead of answering from the local cache. For a
   * decision a stale doc must not make (who holds a capture's lease).
   */
  async getDocFromServer<T = DocumentData>(path: string): Promise<T | undefined> {
    const snap = await getDocFromServer(this.doc<T>(path));
    return snap.exists() ? (snap.data() as T) : undefined;
  }

  async listDocs<T = DocumentData>(
    collectionPath: string,
    spec: QuerySpec = {},
  ): Promise<{ id: string; data: T }[]> {
    const snap = await getDocs(this.buildQuery<T>(collectionPath, spec));
    return snap.docs.map((d) => ({ id: d.id, data: d.data() as T }));
  }

  setDoc<T extends DocumentData>(path: string, data: WithFieldValue<T>): Promise<void> {
    return setDoc(this.doc<T>(path), data);
  }

  updateDoc<T extends DocumentData>(path: string, data: UpdateData<T>): Promise<void> {
    return updateDoc(this.doc<T>(path), data);
  }

  async addDoc<T extends DocumentData>(
    collectionPath: string,
    data: WithFieldValue<T>,
  ): Promise<string> {
    const ref = await addDoc(this.collection<T>(collectionPath), data);
    return ref.id;
  }

  deleteDoc(path: string): Promise<void> {
    return deleteDoc(this.doc(path));
  }

  /** RxJS wrapper around Firestore `onSnapshot` for a query. */
  watchCollection<T = DocumentData>(
    collectionPath: string,
    spec: QuerySpec = {},
  ): Observable<{ id: string; data: T }[]> {
    const q = this.buildQuery<T>(collectionPath, spec);
    return new Observable((subscriber) => {
      const unsub: Unsubscribe = onSnapshot(
        q,
        (snap) => {
          subscriber.next(snap.docs.map((d) => ({ id: d.id, data: d.data() as T })));
        },
        (err) => subscriber.error(err),
      );
      return () => unsub();
    });
  }

  /**
   * RxJS wrapper around Firestore `onSnapshot` for a single document.
   * Emits `undefined` when the document does not exist.
   */
  watchDoc<T = DocumentData>(path: string): Observable<T | undefined> {
    return new Observable((subscriber) => {
      const unsub: Unsubscribe = onSnapshot(
        this.doc<T>(path),
        (snap) => subscriber.next(snap.exists() ? (snap.data() as T) : undefined),
        (err) => subscriber.error(err),
      );
      return () => unsub();
    });
  }
}
